import * as THREE from 'three';
import { ballRadius } from '../sim/world.js';

/**
 * ポケット候補グロー（自前・GPL 非依存）:
 *   自動照準が「狙える」と判定したポケット候補を、半透明の緑円板で可視化する。
 *   選択中のポケット（preferredPocketIndex）はより濃く・大きく表示し、タップ可能であることを示唆。
 *   タップ判定自体は controls.ts 側が canvas pointerup で行う（pocket 中心 + 半径で当たり判定）。
 *
 * `update(positions, selectedIndex)` で候補のリストを更新（毎フレーム）、`hide()` で全非表示。
 * positions は「狙える」と判定されたポケットの中心座標のみが入る配列。selectedIndex はその配列内
 * インデックス（null=未選択＝最厚自動）。
 */

export type Vec2 = { readonly x: number; readonly y: number };

export interface PocketGlowsHandle {
  update(positions: ReadonlyArray<Vec2>, selectedIndex: number | null): void;
  hide(): void;
  dispose(): void;
}

const GLOW_COLOR_DEFAULT = 0x66ff66; // 候補（淡い緑）
const GLOW_COLOR_SELECTED = 0x22ff44; // 選択中（濃い緑）
const GLOW_OPACITY_DEFAULT = 0.35;
const GLOW_OPACITY_SELECTED = 0.7;
const RADIUS_DEFAULT = ballRadius * 1.5;
const RADIUS_SELECTED = ballRadius * 1.9;
const GLOW_Z = -ballRadius + 0.0014; // クロス直上（カット角ガイドより少し上）

/** 6 ポケット分のグロー mesh を用意し、update で表示する候補だけ visible にする。 */
export const createPocketGlows = (scene: THREE.Scene): PocketGlowsHandle => {
  const POCKET_COUNT = 6; // 9ft プール台は 4 コーナー + 2 サイド = 6
  const meshes: THREE.Mesh[] = [];
  const materials: THREE.MeshBasicMaterial[] = [];
  const geometries: THREE.CircleGeometry[] = [];

  for (let i = 0; i < POCKET_COUNT; i++) {
    const geom = new THREE.CircleGeometry(RADIUS_DEFAULT, 32);
    const mat = new THREE.MeshBasicMaterial({
      color: GLOW_COLOR_DEFAULT,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    const mesh = new THREE.Mesh(geom, mat);
    mesh.position.z = GLOW_Z;
    mesh.visible = false;
    mesh.renderOrder = 3;
    mesh.frustumCulled = false;
    scene.add(mesh);
    meshes.push(mesh);
    materials.push(mat);
    geometries.push(geom);
  }

  const update = (
    positions: ReadonlyArray<Vec2>,
    selectedIndex: number | null,
  ): void => {
    // positions の数だけ表示。残りは非表示。最大 POCKET_COUNT までクランプ。
    const n = Math.min(positions.length, POCKET_COUNT);
    for (let i = 0; i < POCKET_COUNT; i++) {
      const mesh = meshes[i]!;
      const mat = materials[i]!;
      if (i >= n) {
        mesh.visible = false;
        continue;
      }
      const p = positions[i]!;
      mesh.position.set(p.x, p.y, GLOW_Z);
      mesh.visible = true;
      const isSelected = selectedIndex === i;
      // 半径はジオメトリのスケールで切替（mesh.scale を使う：geom 再生成なし）。
      const r = isSelected ? RADIUS_SELECTED : RADIUS_DEFAULT;
      mesh.scale.setScalar(r / RADIUS_DEFAULT);
      mat.color.setHex(isSelected ? GLOW_COLOR_SELECTED : GLOW_COLOR_DEFAULT);
      mat.opacity = isSelected ? GLOW_OPACITY_SELECTED : GLOW_OPACITY_DEFAULT;
    }
  };

  const hide = (): void => {
    for (const m of meshes) m.visible = false;
  };

  const dispose = (): void => {
    for (let i = 0; i < POCKET_COUNT; i++) {
      scene.remove(meshes[i]!);
      geometries[i]!.dispose();
      materials[i]!.dispose();
    }
  };

  return { update, hide, dispose };
};
